import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useToast } from "../Toast";
import { useOrgSettingsQuery, type Site } from "../../hooks/useUnits";

// Facilities within this org. With none defined the org is single-site and
// every site control elsewhere in the app stays hidden. Sites are never
// deleted — runs, jobs and harvests keep their site — so a closed facility is
// turned off instead.
export function SitesSection() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Unfiltered list (incl. inactive) — useSites() hides inactive ones, which
  // is right for pickers but not for the place you turn them back on.
  const { data } = useOrgSettingsQuery();
  const sites = data?.sites ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["org-settings"] });

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await api.postJson("/api/sites", { name: name.trim() });
      const body = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) { toast(body.error ?? "Couldn't add that site", "err"); return; }
      setName("");
      await refresh();
      toast("Site added", "ok");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(s: Site) {
    const res = await api.patchJson(`/api/sites/${s.id}`, { active: !s.active });
    if (!res.ok) { toast("Couldn't save", "err"); return; }
    await refresh();
    toast(s.active ? `${s.name} turned off` : `${s.name} turned on`, "ok");
  }

  return (
    <div className="field" style={{ marginBottom: 14 }} data-testid="sites-section">
      <div className="field-label">Sites</div>
      <div className="muted small" style={{ marginBottom: 6 }}>
        Only needed if you run more than one facility. Each device picks its site on the home
        screen, runs are tagged with it, and the board can filter by site.
      </div>
      {sites.length > 0 && (
        <div className="chip-grid" style={{ marginBottom: 8 }}>
          {sites.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`site-toggle-${s.id}`}
              className={`chip${s.active ? " active" : ""}`}
              title={s.active ? "Tap to turn off" : "Tap to turn on"}
              onClick={() => void toggle(s)}
            >
              {s.name}{s.active ? "" : " (off)"}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          data-testid="new-site-name"
          value={name}
          maxLength={60}
          placeholder="e.g. North facility"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
        />
        <button
          type="button"
          className="btn btn-sm"
          data-testid="new-site-submit"
          disabled={busy || !name.trim()}
          onClick={() => void add()}
        >
          + Add site
        </button>
      </div>
    </div>
  );
}
